import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useLocation, useNavigate } from 'react-router';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Stack, Typography } from '@mui/material';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { MenuButton } from '../../../../../pages/Finance2/common-grid-defs/MenuButton';
import { NewKpiCollectionConfigId } from '../../../../../pages/KPI/KpiCollection/KpiCollectionConfigsPage';
import {
  useDeleteKpiCollectionConfiguration,
  useKpiCollectionSettingsState,
} from '../../../../../pages/KPI/KpiCollection/state/kpi-collection-state';
import { colors } from '../../../../../theme/colors';
import { ConfirmDeletionDialog } from '../../../../Dialog/ConfirmDialog/ConfirmDeletionDialog';
import PlusIconButton from '../../../../IconButton/PlusIconButton';
import { SubSectionNavigation } from '../../SubSectionNavigation/SubSectionNavigation';
import { SubNavItem } from '../../SubSectionNavigation/components/SubNavItem/SubNavItem';
import { SubNavSectionWrapper } from '../../SubSectionNavigation/SubSectionNavItems';
import { KpiCollectionConfigurationResponse } from '../../../../../schemas/KpiCollectionConfiguration.schema';

const kpiConfigMenuTitle = 'kpi configuration actions';

const menuButtonCss = css`
  &&&& .MuiButtonBase-root[title='${kpiConfigMenuTitle}'] svg {
    fill: ${colors.secondary[20]};
  }
`;

export function DCConfigurationNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: configurations } = useKpiCollectionSettingsState();
  const { mutateAsync: deleteConfig } = useDeleteKpiCollectionConfiguration();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<Pick<
    KpiCollectionConfigurationResponse,
    'id' | 'name'
  > | null>(null);

  const selectedConfigId = pathname.split('/').pop();
  const isNewConfig = selectedConfigId === NewKpiCollectionConfigId;

  const sections = useMemo(() => {
    const items =
      configurations?.map((config) => ({
        id: config.id,
        value: config.name,
      })) ?? [];

    if (isNewConfig) {
      return [{ id: NewKpiCollectionConfigId, value: 'New Configuration' }, ...items];
    }
    return items;
  }, [configurations, isNewConfig]);

  const handleNavigateNew = useCallback(() => {
    navigate(`/${ROUTES.KPI}/${ROUTES.KPI_DATA_COLLECTION_CONFIGURATION}/${NewKpiCollectionConfigId}`);
  }, [navigate]);

  const handleDeleteClick = useCallback((config: Pick<KpiCollectionConfigurationResponse, 'id' | 'name'>) => {
    setConfigToDelete(config);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setConfigToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (configToDelete) {
      const remainingConfigs = configurations?.filter((c) => c.id !== configToDelete.id) ?? [];
      const targetId = remainingConfigs.length > 0 ? remainingConfigs[0].id : NewKpiCollectionConfigId;

      await deleteConfig(configToDelete.id);
      navigate(`/${ROUTES.KPI}/${ROUTES.KPI_DATA_COLLECTION_CONFIGURATION}/${targetId}`);
    }
    handleCloseDeleteModal();
  }, [configToDelete, configurations, deleteConfig, navigate, handleCloseDeleteModal]);

  const parentRoute = `${ROUTES.KPI}/${ROUTES.KPI_DATA_COLLECTION_CONFIGURATION}`;

  return (
    <>
      <ConfirmDeletionDialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete "${configToDelete?.name}"?`}
      >
        <Typography variant='body2' color='text.secondary'>
          This action cannot be undone.
        </Typography>
      </ConfirmDeletionDialog>
      <SubSectionNavigation
        title='Configurations'
        RightSlot={
          <PlusIconButton
            sx={{
              color: colors.secondary[20],
              '&.Mui-disabled': {
                color: colors.secondary[20],
                opacity: 0.6,
              },
            }}
            onClick={handleNavigateNew}
            disabled={isNewConfig}
          >
            Add New
          </PlusIconButton>
        }
      >
        <SubNavSectionWrapper>
          {sections.map((section) => {
            const isSelected = selectedConfigId === section.id;
            const isNew = section.id === NewKpiCollectionConfigId;
            const originalConfig = configurations?.find((c) => String(c.id) === section.id);

            return (
              <Stack
                key={section.id}
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                css={menuButtonCss}
              >
                <SubNavItem section={section} parentRoute={parentRoute} />
                {isSelected && !isNew && originalConfig && (
                  <MenuButton
                    menuItems={[
                      {
                        name: 'Delete',
                        icon: <DeleteOutlineIcon fontSize='small' />,
                        onClick: () =>
                          handleDeleteClick({ id: originalConfig.id, name: originalConfig.name }),
                      },
                    ]}
                    label='kpi configuration actions'
                  />
                )}
              </Stack>
            );
          })}
        </SubNavSectionWrapper>
      </SubSectionNavigation>
    </>
  );
}
